import {
  B2BPrepaidCampaignCreateRequest,
  B2BPrepaidCampaignUpdateRequest,
  CampaignsApiClient,
  CampaignsApiException,
  CampaignUniqueCodeDetailsDto,
  CampaignUniqueCodeDto,
  CreateUserSegmentCampaignCreateRequest,
  DownloadCodeType,
  FreeDeliveryCampaignCreateRequest,
  LocalMarketingCampaignCreateRequest,
  LocalMarketingCampaignUpdateRequest,
  ProductCouponCampaignCreateRequest,
  RestaurantsClaimsCodesCreateRequest,
  UserSegmentCampaignUpdateRequest,
} from '@kotipizzagroup/kotipizza-campaigns-api-client';
import { getConfiguration } from '../shared/utils/configuration';
import Axios from 'axios';
import { getRequestHeaders, postToSlack } from './utils';
import { ApiCampaign, PostApiCampaign } from '../../../types/global';
import { CampaignTypes } from '../views/CampaignsPage/shared';
import { normalizeCampaignCategory } from '../shared/utils/campaignTypes';

type ConstraintViolation = { propertyName?: string; errorMessage?: string };

type ApiErrorResponseBody = {
  status?: number;
  errorMessage?: string;
  data?: { violations?: ConstraintViolation[] };
};

/**
 * The API answers a rejected create with an ApiErrorResponse whose data.violations carry the
 * field level reasons. Without reading them every failure reaches the UI as a bare "Error
 * creating campaign", which is useless on the batch results list where one row out of fifty is
 * the one that failed.
 *
 * The generated client only parses a response body for HTTP 400, and its throwException throws
 * that parsed body *itself* rather than wrapping it -- so a rejected create arrives as a plain
 * object. A CampaignsApiException is what turns up for everything else, carrying a status and a
 * generic message and nothing more, which is all a 401 or a 500 gives us to report.
 */
const asApiException = (error: unknown): CampaignsApiException | null => {
  // isCampaignsApiException reads a property straight off its argument, so a nullish rejection
  // has to be filtered out first or the real failure becomes a TypeError from this line.
  if (typeof error !== 'object' || error === null) return null;

  return CampaignsApiException.isCampaignsApiException(error) ? error : null;
};

const describeCampaignError = (error: unknown): string => {
  const apiException = asApiException(error);
  const body = (apiException ? apiException.result : error) as ApiErrorResponseBody | undefined;

  const reasons = (body?.data?.violations || [])
    .map((violation) => violation.errorMessage)
    .filter((reason): reason is string => Boolean(reason));

  if (reasons.length > 0) return reasons.join(' ');
  if (body?.errorMessage) return body.status ? `${body.status} ${body.errorMessage}` : body.errorMessage;
  if (apiException) return `${apiException.status} ${apiException.message}`;

  return error instanceof Error ? error.message : String(error);
};

let client: CampaignsApiClient | null = null;

const getApiClient = (): CampaignsApiClient => {
  if (client) return client;

  const baseUrl = getConfiguration<string>('campaignsApi.root', '');

  const instance = Axios.create({
    timeout: 60000,
    transformResponse: [],
  });

  instance.interceptors.request.use(async (config) => {
    const headers = await getRequestHeaders();
    config.headers = headers;
    return config;
  });

  client = new CampaignsApiClient(baseUrl, instance);
  return client;
};

export const fetchCampaignTypes = async (): Promise<string[]> => {
  const client = getApiClient();
  return client.getCampaignCategories();
};

export const fetchCampaigns = async (campaignType: string): Promise<ApiCampaign[]> => {
  const client = getApiClient();
  return client.getCampaignsByCategory(campaignType, false);
};

export const fetchCampaign = async (
  campaignType: CampaignTypes,
  campaignId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  //Return type is any, because it would be hard to get it working with old Campaign pages. New pages are using V2-functions which are properly typed.

  const client = getApiClient();

  switch (campaignType) {
    case CampaignTypes.B2BPrepaidCodes:
      return client.getB2BPrepaidCampaignById(campaignId);
    case CampaignTypes.Combo:
      return client.getComboCampaignById(campaignId);
    case CampaignTypes.FreeDelivery:
      return client.getFreeDeliveryCampaignById(campaignId);
    case CampaignTypes.LocalFreeDelivery:
      return client.getFreeDeliveryCampaignById(campaignId);
    case CampaignTypes.LoyaltyDiscount:
      return client.getLoyaltyDiscountCampaignById(campaignId);
    case CampaignTypes.LoyaltyPrize:
      return client.getLoyaltyPrizeCampaignById(campaignId);
    case CampaignTypes.ProductCoupon:
      return client.getProductCouponCampaignById(campaignId);
    case CampaignTypes.RestaurantClaimCodes:
      return client.getRestaurantClaimCodesCampaignById(campaignId);
    case CampaignTypes.UserSegmentTargetedCampaign:
      return client.getUserSegmentCampaignById(campaignId);
  }

  throw new Error(`Unknown campaign type: ${campaignType}`);
};

export const fetchCampaignV2 = async (campaignType: CampaignTypes, campaignId: string) => {
  const client = getApiClient();

  switch (campaignType) {
    case CampaignTypes.LocalMarketingCampaign:
      return client.getLocalMarketingCampaignById(campaignId);
    case CampaignTypes.ShopSpecificCampaign:
      return client.getShopSpecificCampaignById(campaignId);
    case CampaignTypes.UserSegmentTargetedCampaign:
      return client.getUserSegmentCampaignById(campaignId);
  }

  throw new Error(`Unknown campaign type: ${campaignType}`);
};

export const fetchAllCampaignDeliveryMethods = async (): Promise<string[]> => {
  const client = getApiClient();
  return client.getDeliveryMethods();
};

export const getSingleCampaignCode = async (code: string): Promise<CampaignUniqueCodeDetailsDto> => {
  const client = getApiClient();
  const details = await client.getUniqueCodeDetailsByCode(code);
  return { ...details, campaign: details.campaign && normalizeCampaignCategory(details.campaign) };
};

export const invalidateCampaignCode = async (code: string): Promise<CampaignUniqueCodeDto> => {
  try {
    const client = getApiClient();
    const response = await client.invalidateUniqueCode(code);

    await postToSlack(`Unique code ${code} invalidated`, 'campaigns');

    return response;
  } catch (error) {
    await postToSlack(`Error invalidating unique code ${code}: ${describeCampaignError(error)}`, 'campaigns');
    throw error;
  }
};

export const createShopSpecificCampaign = async (campaignData: LocalMarketingCampaignCreateRequest) => {
  try {
    const client = getApiClient();
    const response = await client.createShopSpecificCampaign(campaignData);

    await postToSlack(`Shop specific campaign ${response.name} created with id: ${response.campaignId}`, 'campaigns');

    return response;
  } catch (error) {
    await postToSlack(`Error creating shop specific campaign: ${error}`, 'campaigns');
    throw new Error('Error creating campaign');
  }
};

export const createLocalMarketingCampaign = async (campaignData: LocalMarketingCampaignCreateRequest) => {
  try {
    const client = getApiClient();
    const response = await client.createLocalMarketingCampaign(campaignData);

    await postToSlack(`Local marketing campaign ${response.name} created with id: ${response.campaignId}`, 'campaigns');

    return response;
  } catch (error) {
    await postToSlack(`Error creating local marketing campaign: ${error}`, 'campaigns');
    throw new Error('Error creating campaign');
  }
};

const createB2BPrepaidCodesCampaign = async (campaignType: string, campaignData: B2BPrepaidCampaignCreateRequest) => {
  try {
    const client = getApiClient();
    const response = await client.createB2BPrepaidCodesCampaign(campaignData);

    await postToSlack(`${campaignType}, ${response.name} created with id: ${response.campaignId}`, 'campaigns');

    return response;
  } catch (error) {
    await postToSlack(`Error creating ${campaignType} campaign: ${error}`, 'campaigns');
    throw new Error('Error creating campaign');
  }
};

const createProductCouponCampaign = async (campaignType: string, campaignData: ProductCouponCampaignCreateRequest) => {
  try {
    const client = getApiClient();
    const response = await client.createProductCouponCampaign(campaignData);

    await postToSlack(`${campaignType}, ${response.name} created with id: ${response.campaignId}`, 'campaigns');

    return response;
  } catch (error) {
    // Carried through rather than flattened: the batch results list names the row that failed,
    // so the reason it failed is the one thing worth reporting there.
    const reason = describeCampaignError(error);
    await postToSlack(`Error creating ${campaignType} campaign: ${reason}`, 'campaigns');
    throw new Error(reason);
  }
};

const createFreeDeliveryCampaign = async (campaignType: string, campaignData: FreeDeliveryCampaignCreateRequest) => {
  try {
    const client = getApiClient();
    const response = await client.createFreeDeliveryCampaign(campaignData);

    await postToSlack(`${campaignType}, ${response.name} created with id: ${response.campaignId}`, 'campaigns');

    return response;
  } catch (error) {
    await postToSlack(`Error creating ${campaignType} campaign: ${error}`, 'campaigns');
    throw new Error('Error creating campaign');
  }
};

const createRestaurantClaimCodesCampaign = async (
  campaignType: string,
  campaignData: RestaurantsClaimsCodesCreateRequest
) => {
  try {
    const client = getApiClient();
    const response = await client.createRestaurantClaimCodesCampaign(campaignData);

    for (const campaign of response) {
      await postToSlack(`${campaignType}, ${campaign.name} created with id: ${campaign.campaignId}`, 'campaigns');
    }

    return response;
  } catch (error) {
    await postToSlack(`Error creating ${campaignType} campaign: ${error}`, 'campaigns');
    throw new Error('Error creating campaign');
  }
};

const createUserSegmentCampaign = async (
  campaignType: string,
  campaignData: CreateUserSegmentCampaignCreateRequest
) => {
  try {
    const client = getApiClient();
    const response = await client.createUserSegmentCampaign(campaignData);

    await postToSlack(`${campaignType}, ${response.name} created with id: ${response.campaignId}`, 'campaigns');

    return response;
  } catch (error) {
    await postToSlack(`Error creating ${campaignType} campaign: ${error}`, 'campaigns');
    throw new Error('Error creating campaign');
  }
};

export const createCampaign = async (campaignType: string, campaignData: unknown) => {
  switch (campaignType) {
    case CampaignTypes.ProductCoupon:
      return createProductCouponCampaign(campaignType, campaignData as ProductCouponCampaignCreateRequest);
    case CampaignTypes.B2BPrepaidCodes:
      return createB2BPrepaidCodesCampaign(campaignType, campaignData as B2BPrepaidCampaignCreateRequest);
    case CampaignTypes.FreeDelivery:
      return createFreeDeliveryCampaign(campaignType, campaignData as FreeDeliveryCampaignCreateRequest);
    case CampaignTypes.LocalFreeDelivery:
      return createFreeDeliveryCampaign(campaignType, campaignData as FreeDeliveryCampaignCreateRequest);
    case CampaignTypes.RestaurantClaimCodes:
      return createRestaurantClaimCodesCampaign(campaignType, campaignData as RestaurantsClaimsCodesCreateRequest);
    case CampaignTypes.UserSegmentTargetedCampaign:
      return createUserSegmentCampaign(campaignType, campaignData as CreateUserSegmentCampaignCreateRequest);
  }
};

export const updateCampaign = async (campaignType: string, campaignData: PostApiCampaign) => {
  const client = getApiClient();

  switch (campaignType) {
    case CampaignTypes.B2BPrepaidCodes:
      return client.updateB2BPrepaidCodeCampaign(campaignData as B2BPrepaidCampaignUpdateRequest);
    case CampaignTypes.FreeDelivery:
      return client.updateFreeDeliveryCampaign(campaignData);
    case CampaignTypes.LocalFreeDelivery:
      return client.updateFreeDeliveryCampaign(campaignData);
    case CampaignTypes.LocalMarketingCampaign:
      return client.updateLocalMarketingCampaign(campaignData);
    case CampaignTypes.LoyaltyDiscount:
      return client.updateLocalMarketingCampaign(campaignData);
    case CampaignTypes.ProductCoupon:
      return client.updateProductCouponCampaign(campaignData);
    case CampaignTypes.ShopSpecificCampaign:
      return client.updateShopSpecificCampaign(campaignData);
    case CampaignTypes.UserSegmentTargetedCampaign:
      return client.updateUserSegmentCampaign(campaignData);
  }
};

export const deactivateCampaignById = async (campaignId: string) => {
  const client = getApiClient();

  return client.deactivateCampaignById(campaignId);
};

export const updateCampaignV2 = async (campaignType: string, campaignData: LocalMarketingCampaignUpdateRequest) => {
  const client = getApiClient();

  switch (campaignType) {
    case CampaignTypes.LocalMarketingCampaign:
      return client.updateLocalMarketingCampaign(campaignData);
    case CampaignTypes.ShopSpecificCampaign:
      return client.updateShopSpecificCampaign(campaignData);
  }
};

export const updateUserSegmentCampaign = async (campaignData: UserSegmentCampaignUpdateRequest) => {
  const client = getApiClient();

  return client.updateUserSegmentCampaign(campaignData);
};

export const fetchClaimCodesCsv = async (campaignIds: string[]) => {
  const client = getApiClient();
  return client.downloadClaimCodesCsv({
    campaignIds,
  });
};

export const fetchCampaignCodesExcel = async (campaignId: string, downloadCodeType: DownloadCodeType) => {
  const client = getApiClient();
  return client.downloadUniqueCodesExcelFile(campaignId, downloadCodeType);
};

export const updateCampaignDisplayOrder = async (campaignId: string, displayOrder: number) => {
  const client = getApiClient();
  return client.setCampaignDisplayOrder(campaignId, displayOrder);
};

export const fetchUniqueCodesOfUser = async (userId: number) => {
  const client = getApiClient();
  const details = await client.getPersonalDiscountsOfUserById(userId);
  return { ...details, campaigns: details.campaigns?.map(normalizeCampaignCategory) };
};
